import { Professional, ProfessionalLicense, ProfessionalSpecialty } from '@medprev-web-app/api-graphql-client';

import { SchedulesSearchProfessionalLicenses } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { ReviewerSpecialty } from '@/types/post-reviewer.types';

export function hasAnyLicenseWithCRM(professional: { licenses?: ProfessionalLicense[] }): boolean {
  return professional.licenses?.some((license) => license.certificationBodyInitials === 'CRM') || false;
}

export function hasGeneralistSpecialty(professional: { specialties?: ProfessionalSpecialty[] | null }): boolean {
  return professional.specialties?.some((specialty) => specialty.name?.toLowerCase() === 'médico generalista') || false;
}

export function hasCRM(licenses: SchedulesSearchProfessionalLicenses[]): boolean {
  return !!licenses?.find((license) => license.professionalLicenseCRM);
}

export const formatCRMInfo = (license: SchedulesSearchProfessionalLicenses): string => {
  const { professionalLicenseCRM, professionalLicenseState } = license;
  return `CRM ${professionalLicenseCRM} - ${professionalLicenseState} • MÉDICO`;
};

export const rqeDisplay = (license: SchedulesSearchProfessionalLicenses): string => {
  const { specialtyName, professionalLicenseCRM, specialistQualificationRecord } = license;
  const isGeneralist = specialtyName?.toLowerCase() === 'médico generalista';

  if (isGeneralist) {
    return `${specialtyName} CRM ${professionalLicenseCRM}`;
  }

  if (!specialistQualificationRecord) {
    return `${specialtyName} CRM ${professionalLicenseCRM} - Não possui RQE para ${specialtyName.toLocaleUpperCase()}`;
  }

  return `${specialtyName} CRM ${professionalLicenseCRM} - RQE ${specialistQualificationRecord}`;
};

export const blogRQEDisplay = (specialty: ReviewerSpecialty, hasCrm: boolean): string => {
  const { name, specialistQualificationRecord } = specialty;
  const isGeneralist = name?.toLowerCase() === 'médico generalista';
  if (isGeneralist) {
    return 'Médico Generalista';
  }

  if (!specialistQualificationRecord && hasCrm) {
    return `${name} - Não possui RQE para ${name.toLocaleUpperCase()}`;
  }

  return `${name} - RQE ${specialistQualificationRecord}`;
};

export const formatProfessionalLicenses = (licenses: ProfessionalLicense[]): string => {
  return licenses
    .map((license) => `${license.certificationBodyInitials}/${license.licenseState} - ${license.licenseRecord}`)
    .join(', ');
};

export const formatExperiences = (professional: Professional): string[] => {
  const hasCRM = hasAnyLicenseWithCRM(professional);
  const isGeneralist = hasGeneralistSpecialty(professional);
  const nonGeneralistSpecialties = professional?.specialties?.filter(
    (specialty) => specialty?.name?.toLowerCase() !== 'médico generalista',
  );

  const formatted = nonGeneralistSpecialties
    ?.map((specialty) => {
      const rqe = specialty.specialistQualificationRecord;

      if (hasCRM && !rqe) {
        return `${specialty.name} - Não possui RQE para ${specialty.name!.toLocaleUpperCase()}`;
      }
    })
    .filter(Boolean) as string[];

  if (isGeneralist) {
    formatted.unshift('Médico Generalista');
  }

  return formatted;
};

export const formatSpecialties = (professional: Professional): string[] => {
  const { specialties } = professional;

  if (!specialties || specialties.length === 0) {
    return [];
  }

  const hasCRM = hasAnyLicenseWithCRM(professional);
  const isGeneralist = hasGeneralistSpecialty(professional);

  const nonGeneralistSpecialties = specialties.filter(
    (specialty) => specialty.name!.toLowerCase() !== 'médico generalista',
  );

  const formatted = nonGeneralistSpecialties.map((specialty) => {
    if (!hasCRM) {
      return specialty.name;
    }

    const rqe = specialty.specialistQualificationRecord;
    return `${specialty.name} - ${rqe ? `RQE ${rqe}` : 'sem RQE'}`;
  });

  if (isGeneralist) {
    formatted.unshift('Médico Generalista');
  }

  return formatted as string[];
};

export function formatReviewerSpecialties(specialties: ReviewerSpecialty[] | undefined, hasCrm: boolean): string {
  if (!specialties || specialties.length === 0) {
    return '';
  }
  return specialties
    .map((specialty) => blogRQEDisplay(specialty, hasCrm))
    .filter(Boolean)
    .join(', ');
}
